/* sb-dc-imgblob.js - keep uploaded photos OUT of the design document.
   2026-09-17 (v2 - the in-flight lock can now time out; see STUCK LOCK).

   WHAT IS WRONG TODAY (measured 2026-09-17, live page, 6000x4000 JPEG 12.72MB)
   The app stores an uploaded photo in the layer as a base64 data: URL, so the
   bytes live inside the document itself:

     layer.src length      17,785,967 characters   (base64 is ~33% bigger)
     JSON.stringify(doc)   17.83 MB

   That one string is behind three separate problems:

     1. Add to cart is slow - the whole 17.8MB document is serialised and sent.
        sb-dc-aug.js also drops designJson over 9.5MB, so on a big design the
        design snapshot is thrown away anyway.
     2. Autosave is DEAD on exactly the designs people care about.
        sb-dc-autosave.js skips anything over MAX_BYTES (2.5MB). Measured on
        the live page with this photo:
            __dcAutoSave() -> { saves: 59, bytes: 914694,
                                why: "too large to save locally" }
        It saved fine up to 0.91MB and stopped the moment the photo landed.
        Close the tab and the work is gone - for every customer who uploads a
        photo, which is most of them.
     3. Memory - the same picture is held twice, once as a 17.8MB string and
        once as the decoded bitmap.

   WHAT THIS DOES
   The bytes move out of the document and into the browser's own storage:

     - the photo is put in IndexedDB (hundreds of MB available, unlike the ~5MB
       localStorage the autosave has to fit in);
     - the layer gets a blob: URL instead - a short handle to those bytes;
     - a small map of blob URL -> IndexedDB key is kept in localStorage so the
       photo can be found again after a reload.

   Measured with this file in place, same photo:
     document        17.83MB -> 0.0006MB      layer.src  17,785,967 -> 69 chars
     autosave        "too large to save locally" -> saving normally
     fresh tab       restore brings the photo back at 6000x4000
     print path      getImageData / toDataURL / Konva stage export all fine
     size panel      still reads "Original 6000 x 4000 px, 130 DPI"

   Nothing about the picture changes. A blob: URL is the same bytes at the same
   resolution; the canvas draws it identically, the print file is rendered from
   the same pixels, and the resolution warning sees the same dimensions. This
   file does NOT downscale anything - that is a separate job with a separate
   risk, and it is not what this one does.

   WHY A WATCHER AND NOT A WRAPPER
   The upload does not go through api.addLayer - measured: a file dropped on
   the picker adds a layer with no api call at all, the app writes to its own
   store. So there is nothing to wrap; the document is watched instead and a
   data: URL is swapped for a blob: URL as soon as one appears.

   AFTER A RELOAD
   A blob: URL only lives as long as the page. When the autosave restores a
   design, its blob URLs are dead - fetch() on them fails. This file notices
   that, looks the URL up in the map, reads the bytes back out of IndexedDB and
   hands the layer a fresh blob URL. That is what makes "Pick up where I left
   off" work with a photo in the design, which it does not today.
   (Note when testing: a plain reload can leave the old blob URL alive, so the
   restore path is only really exercised from a NEW tab.)

   STUCK LOCK (found while testing, 2026-09-17)
   v1 set a per-layer busy flag and cleared it in the promise callbacks. During
   a heavy canvas operation the tab froze long enough that a conversion never
   settled, the flag stayed set, and that photo was never converted - the
   document stayed at 17MB with no error anywhere (converted: 0, failed: 0).
   The lock now records WHEN it was taken and is released after LOCK_MS so the
   next tick tries again, up to MAX_TRIES per layer. A photo that cannot be
   converted after that is left as a data: URL, which is today's behaviour.

   SAFETY
     - same-origin blob URLs do not taint a canvas, so toBlob / toDataURL for
       the print file keep working;
     - if IndexedDB is unavailable (private mode, quota), the data: URL is left
       exactly as it is - today's behaviour, not a new failure;
     - only images over MIN_BYTES are touched, so template artwork and small
       icons are left alone;
     - identical bytes are stored once (fingerprint), so undo/redo does not
       pile up copies.

   Diagnostics: window.__dcImgBlob() */
(function () {
  'use strict';

  var DB = 'sbdc-img', STORE = 'files', MAP_KEY = 'sbdc_imgmap_v1';
  var TICK = 400;
  var LOCK_MS = 20000;           // an in-flight conversion that never settled
  var MAX_TRIES = 3;
  var MIN_BYTES = 100000;        // ~75KB of real image; below this leave it alone
  var MAX_AGE_MS = 14 * 864e5;   // same window the autosave offers a design in

  var info = {
    converted: 0, bytesOut: 0, reused: 0, restored: 0, failed: 0,
    retries: 0, gaveUp: 0, idb: false, why: '', last: null
  };
  window.__dcImgBlob = function () { return info; };

  /* ---- IndexedDB -------------------------------------------------------- */
  var dbp = null;
  function db() {
    if (dbp) return dbp;
    dbp = new Promise(function (res, rej) {
      var r = indexedDB.open(DB, 1);
      r.onupgradeneeded = function () {
        if (!r.result.objectStoreNames.contains(STORE)) r.result.createObjectStore(STORE);
      };
      r.onsuccess = function () { info.idb = true; res(r.result); };
      r.onerror = function () { rej(r.error); };
    }).catch(function (e) {
      info.why = 'idb unavailable: ' + (e && e.message);
      return null;
    });
    return dbp;
  }

  function idbPut(key, blob) {
    return db().then(function (d) {
      if (!d) return false;
      return new Promise(function (res) {
        var tx = d.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put({ blob: blob, at: Date.now(), type: blob.type }, key);
        tx.oncomplete = function () { res(true); };
        tx.onerror = function () { res(false); };
      });
    });
  }

  function idbGet(key) {
    return db().then(function (d) {
      if (!d) return null;
      return new Promise(function (res) {
        var tx = d.transaction(STORE, 'readonly');
        var q = tx.objectStore(STORE).get(key);
        q.onsuccess = function () { res(q.result || null); };
        q.onerror = function () { res(null); };
      });
    });
  }

  function idbSweep() {
    db().then(function (d) {
      if (!d) return;
      var tx = d.transaction(STORE, 'readwrite');
      var st = tx.objectStore(STORE);
      var cur = st.openCursor();
      cur.onsuccess = function () {
        var c = cur.result;
        if (!c) return;
        var v = c.value;
        if (!v || Date.now() - (v.at || 0) > MAX_AGE_MS) c.delete();
        c.continue();
      };
    });
  }

  /* ---- url -> key map (small, survives reload) -------------------------- */
  function readMap() {
    try { return JSON.parse(localStorage.getItem(MAP_KEY) || '{}') || {}; }
    catch (e) { return {}; }
  }
  function writeMap(m) {
    try { localStorage.setItem(MAP_KEY, JSON.stringify(m)); } catch (e) {}
  }
  function remember(url, key, meta) {
    var m = readMap();
    m[url] = { key: key, at: Date.now(), type: meta.type || '', size: meta.size || 0 };
    /* keep it small: 40 newest entries */
    var keys = Object.keys(m);
    if (keys.length > 40) {
      keys.sort(function (a, b) { return (m[b].at || 0) - (m[a].at || 0); });
      var trimmed = {};
      keys.slice(0, 40).forEach(function (k) { trimmed[k] = m[k]; });
      m = trimmed;
    }
    writeMap(m);
  }

  /* ---- fingerprint so the same bytes are stored once --------------------- */
  var byPrint = {};              // fingerprint -> blob url, this page only
  function print(src) {
    return src.length + ':' + src.slice(24, 88) + ':' + src.slice(-48);
  }

  /* ---- the swap --------------------------------------------------------- */
  var busy = {}, tries = {};

  /* True while a conversion for this layer is genuinely in flight. A lock older
     than LOCK_MS is treated as lost (frozen tab, a promise that never settled)
     and the layer is tried again. */
  function locked(id) {
    var at = busy[id];
    if (!at) return false;
    if (Date.now() - at < LOCK_MS) return true;
    busy[id] = 0;
    info.retries++;
    return false;
  }

  function convert(api, layer) {
    var id = layer.id, src = layer.src;
    if (locked(id)) return;
    if ((tries[id] || 0) >= MAX_TRIES) {
      if (tries[id] === MAX_TRIES) { tries[id]++; info.gaveUp++; info.why = 'gave up on ' + id; }
      return;
    }
    tries[id] = (tries[id] || 0) + 1;
    busy[id] = Date.now();

    var fp = print(src);
    if (byPrint[fp]) {                       // undo/redo brought the same photo back
      try { api.updateLayer(id, { src: byPrint[fp] }); info.reused++; } catch (e) {}
      busy[id] = 0;
      return;
    }

    fetch(src).then(function (r) { return r.blob(); }).then(function (blob) {
      var key = 'img_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
      return idbPut(key, blob).then(function (ok) {
        var url = URL.createObjectURL(blob);
        byPrint[fp] = url;
        if (ok) remember(url, key, blob);
        var a = window.__dcApi;
        if (a && typeof a.updateLayer === 'function') a.updateLayer(id, { src: url });
        info.converted++;
        info.bytesOut += blob.size;
        info.last = { id: id, mb: +(blob.size / 1048576).toFixed(2), stored: ok };
        busy[id] = 0;
      });
    }).catch(function (e) {
      info.failed++;
      info.why = 'convert failed: ' + (e && e.message);
      busy[id] = 0;                          // leave the data: URL in place
    });
  }

  /* ---- bringing a restored design back to life -------------------------- */
  function rehydrate(api, layer) {
    var id = layer.id, url = layer.src;
    if (locked(id)) return;
    busy[id] = Date.now();

    fetch(url).then(function () {            // still alive - nothing to do
      busy[id] = 0;
    }).catch(function () {
      var m = readMap(), rec = m[url];
      if (!rec) { busy[id] = 0; return; }
      idbGet(rec.key).then(function (row) {
        if (!row || !row.blob) { busy[id] = 0; return; }
        var fresh = URL.createObjectURL(row.blob);
        remember(fresh, rec.key, row.blob);
        var a = window.__dcApi;
        /* 2026-09-23: every layer showing this picture gets the fresh address, not only the first one seen
           (a repeated logo, a duplicated photo share one address; the loop below lets a dead address through once).
           No history entry: the picture itself did not change. */
        if (a && typeof a.updateLayer === 'function') ['front', 'back'].forEach(function (side) {
          var sd = a[side]; if (!sd || !Array.isArray(sd.layers)) return;
          sd.layers.forEach(function (l) { if (l && l.src === url) a.updateLayer(l.id, { src: fresh }, false); });
        });
        info.restored++;
        busy[id] = 0;
      }).catch(function () { busy[id] = 0; });
    });
  }

  /* ---- loop ------------------------------------------------------------- */
  var seenBlob = {};
  setInterval(function () {
    var api = window.__dcApi;
    if (!api || typeof api.updateLayer !== 'function') return;

    ['front', 'back'].forEach(function (side) {
      var sd = api[side];
      if (!sd || !Array.isArray(sd.layers)) return;
      sd.layers.forEach(function (l) {
        if (!l || l.type !== 'image') return;
        var src = l.src;
        if (typeof src !== 'string') return;

        if (src.slice(0, 11) === 'data:image/') {
          if (src.length < MIN_BYTES) return;
          convert(api, l);
        } else if (src.slice(0, 5) === 'blob:' && !seenBlob[src]) {
          seenBlob[src] = 1;
          rehydrate(api, l);
        }
      });
    });
  }, TICK);

  setTimeout(idbSweep, 8000);
})();
